
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { FileCheck2, ScrollText, Building, GraduationCap } from 'lucide-react';

const HRCompliancePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>PEO HR Compliance Services | The PEO App</title>
        <meta name="description" content="Navigate complex employment regulations with PEO HR compliance services. Learn how Professional Employer Organizations help businesses reduce risk and stay compliant with evolving regulations." />
        <link rel="canonical" href="https://thepeoapp.com/services/hr-compliance" />
        <meta name="keywords" content="PEO HR compliance, HR compliance services, employment law compliance, workplace regulations, HR compliance solution, Professional Employer Organization compliance" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">PEO HR Compliance Services</h1>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Navigating the complex landscape of employment laws and regulations is one of the greatest challenges for today's businesses. Professional Employer Organizations (PEOs) provide expert HR compliance support to help businesses mitigate risk, avoid costly penalties, and create legally-sound workplace policies and practices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <FileCheck2 className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Employment Law Compliance</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Federal, state, and local employment law guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Wage and hour compliance (FLSA)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Equal employment opportunity compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Leave management (FMLA, ADA, state-specific leaves)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>I-9 verification and management</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <ScrollText className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Policy Development & Documentation</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Customized employee handbooks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>HR policy development and updating</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Compliant job descriptions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Required workplace postings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Documentation retention guidance</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <Building className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Workplace Compliance</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Workplace harassment prevention</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Safety and OSHA compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Drug-free workplace programs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Background checking compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Workplace investigation guidance</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <GraduationCap className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Training & Prevention</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Sexual harassment prevention training</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Manager compliance training</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Regulatory updates and newsletters</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>HR compliance audits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Proactive compliance risk assessments</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Why HR Compliance Matters</h2>
            <div className="space-y-6">
              <p className="text-lg text-gray-700">
                Partnering with a PEO for HR compliance delivers significant benefits:
              </p>
              
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li><strong>Risk Reduction:</strong> Minimize exposure to costly litigation, penalties, and fines</li>
                <li><strong>Regulatory Navigation:</strong> Stay current with complex and constantly changing employment laws</li>
                <li><strong>Multi-State Management:</strong> Simplify compliance across multiple jurisdictions for distributed workforces</li>
                <li><strong>Expert Support:</strong> Access HR specialists with deep compliance knowledge without adding headcount</li>
                <li><strong>Proactive Protection:</strong> Identify and address potential compliance issues before they become problems</li>
                <li><strong>Focus on Growth:</strong> Dedicate more time to business objectives with confidence in your compliance foundation</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Strengthen Your HR Compliance?</h2>
            <Link to="/">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Free PEO Quotes
              </Button>
            </Link>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold mb-6">HR Compliance FAQs</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-2">How does a PEO reduce my compliance risks?</h3>
                <p className="text-gray-700">
                  PEOs monitor changing regulations at federal, state, and local levels and proactively implement necessary adjustments to your HR policies and practices. They provide expert guidance on compliance matters, help develop legally sound documentation, and assist with creating internal processes that promote ongoing compliance.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">What HR compliance challenges do PEOs help address for multi-state employers?</h3>
                <p className="text-gray-700">
                  PEOs are particularly valuable for businesses with employees in multiple states. They help navigate varying state-specific requirements for paid leave, minimum wage, overtime, termination procedures, and mandatory training. PEOs ensure your policies and practices comply with each location's specific regulations.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">Does working with a PEO transfer all compliance liability?</h3>
                <p className="text-gray-700">
                  While PEOs share certain employment responsibilities and liabilities, they don't absorb all compliance risks. The PEO-client relationship is a partnership where both parties have responsibilities. However, PEOs significantly reduce your risk through their expertise, proactive compliance management, and shared liability in many areas of employment law.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HRCompliancePage;
